/**
 * components/Layout.js
 * ─────────────────────
 * Mobile-friendly shell with unified drawer navigation.
 *
 * Desktop: drawer pinned open by default (toggle pin to auto-hide on mouse leave)
 * Mobile:  drawer always auto-hides, toggled via hamburger, closed via backdrop
 *
 *   <AppShell>      — full-page flex column wrapper
 *   <TopNav>        — single sticky header (logo + breadcrumb + hamburger + optional helpButton)
 *   <NavDrawer>     — slide-out drawer (workspace switcher + nav + settings + sign out)
 *   <PageBody>      — center scrollable content area
 *   <BodyGrid>      — flex row layout host (replaces LeftNav + RightBar pattern)
 *   <SectionHeader> — reusable section title row
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWorkspaces } from '../hooks/useWorkspaces';
import { AnimatedDot, showToast } from './UI';
import { apiFetch } from '../services/api';

// ─── constants ────────────────────────────────────────────────────────────────
const DRAWER_WIDTH    = 260;
const MOBILE_BP       = 768;
const PIN_STORAGE_KEY = 'raji_drawer_pinned';

// ─── helpers ──────────────────────────────────────────────────────────────────
function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < MOBILE_BP);
  useEffect(() => {
    const handler = () => setMobile(window.innerWidth < MOBILE_BP);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return mobile;
}

// ─────────────────────────────────────────────────────────────────────────────
// AppShell
// ─────────────────────────────────────────────────────────────────────────────
export function AppShell({ children }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      minHeight: '100vh', height: 'auto',
      overflow: 'visible', background: 'var(--paper)',
    }}>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// NavDrawer
// ─────────────────────────────────────────────────────────────────────────────
export function NavDrawer({ open, onClose, pinned, onTogglePin, agentRunning = false }) {
  const maybeClose = useCallback(() => { if (!pinned) onClose(); }, [pinned, onClose]);
  const location                = useLocation();
  const navigate                = useNavigate();
  const { logout }              = useAuth();
  const { workspaces, activeWorkspace, switchWorkspace } = useWorkspaces();
  const isMobile                = useIsMobile();
  const leaveTimerRef           = useRef(null);

  const wsId   = activeWorkspace?.id || '';
  const wsName = activeWorkspace?.name || 'My Business';

  const navItems = [
    { icon: '📝', label: 'CEO Report',       href: `/dashboard/ceo-runlog?workspace_id=${wsId}` },
//    { icon: '⚡', label: 'Dashboard',        href: '/dashboard' },
    { icon: '✓',  label: 'Tasks',            href: `/dashboard/tasks?workspace_id=${wsId}` },
    { icon: '📄', label: 'Documents',        href: '/dashboard/artifacts' },
    { icon: '🏢', label: 'Business Profile', href: `/workspace/profile?workspace_id=${wsId}` },
  ];

  const updateWorkspaceInUrl = useCallback((wsId) => {
    const url = new URL(window.location.href);
    url.searchParams.set('workspace_id', wsId);
    navigate(url.pathname + url.search, { replace: true });
  }, [navigate]);

  function isActive(href) {
    const path = href.split('?')[0];
    if (path === '/dashboard') return location.pathname === '/dashboard';
    return location.pathname.startsWith(path);
  }

  function handleMouseLeave() {
    if (isMobile || pinned) return;
    leaveTimerRef.current = setTimeout(onClose, 300);
  }
  function handleMouseEnter() { clearTimeout(leaveTimerRef.current); }
  useEffect(() => () => clearTimeout(leaveTimerRef.current), []);

  const drawerStyle = {
    position: 'fixed', top: 0, left: 0,
    width: `${DRAWER_WIDTH}px`, height: '100vh',
    background: 'var(--paper)', borderRight: '1px solid var(--paper-3)',
    display: 'flex', flexDirection: 'column', zIndex: 200,
    transform: open ? 'translateX(0)' : `translateX(-${DRAWER_WIDTH}px)`,
    transition: 'transform 0.22s cubic-bezier(0.4,0,0.2,1)',
    boxShadow: open ? '4px 0 24px rgba(0,0,0,0.08)' : 'none',
    willChange: 'transform',
  };

  return (
    <>
      {open && (isMobile || !pinned) && (
        <div
          onClick={onClose}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.25)',
            zIndex: 199, animation: 'fadeIn 0.18s ease',
          }}
        />
      )}

      <nav style={drawerStyle} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
        {/* Drawer header */}
        <div style={{
          height: '52px', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between', padding: '0 16px',
          borderBottom: '1px solid var(--paper-3)', flexShrink: 0,
        }}>
          <Link to="/dashboard" style={{ fontFamily: 'var(--serif)', fontSize: '20px', color: 'var(--ink)', textDecoration: 'none' }}>
            raji<span style={{ color: 'var(--accent)' }}>.</span>ai
          </Link>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            {!isMobile && (
              <button
                onClick={onTogglePin}
                title={pinned ? 'Unpin sidebar' : 'Pin sidebar open'}
                style={{
                  width: '28px', height: '28px', borderRadius: '6px',
                  background: pinned ? 'var(--paper-3)' : 'none',
                  border: '1px solid transparent', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '14px', color: pinned ? 'var(--ink)' : 'var(--ink-mute)',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--paper-3)'}
                onMouseLeave={e => e.currentTarget.style.background = pinned ? 'var(--paper-3)' : 'none'}
              >
                {pinned ? '📌' : '📍'}
              </button>
            )}
          </div>
        </div>

        {/* Workspace section */}
        <div style={{ padding: '16px', borderBottom: '1px solid var(--paper-3)', flexShrink: 0 }}>
          <div style={{
            fontSize: '10px', fontWeight: '500', letterSpacing: '0.1em',
            textTransform: 'uppercase', color: 'var(--ink-mute)', marginBottom: '8px',
          }}>
            Business
          </div>
          {workspaces.length === 0 ? (
            <WsItem name="My First Business" active={true} onClick={() => { navigate('/dashboard'); maybeClose(); }} />
          ) : workspaces.length <= 2 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {workspaces.map(ws => (
                <WsItem
                  key={ws.id}
                  name={ws.name}
                  active={ws.id === activeWorkspace?.id}
                  onClick={() => {
                    if (ws.id === activeWorkspace?.id) { navigate('/dashboard'); }
                    else { switchWorkspace(ws); updateWorkspaceInUrl(ws.id); }
                    maybeClose();
                  }}
                />
              ))}
            </div>
          ) : (
            <select
              value={activeWorkspace?.id || ''}
              onChange={e => {
                const ws = workspaces.find(w => w.id === e.target.value);
                if (ws) {
                  if (ws.id === activeWorkspace?.id) { navigate('/dashboard'); }
                  else { switchWorkspace(ws); updateWorkspaceInUrl(ws.id); }
                }
                maybeClose();
              }}
              style={{
                width: '100%', padding: '6px 10px', fontSize: '13px',
                border: '1px solid var(--paper-3)', borderRadius: 'var(--r)',
                background: 'var(--white)', color: 'var(--ink)',
                fontFamily: 'var(--sans)', cursor: 'pointer',
              }}
            >
              {workspaces.map(ws => (
                <option key={ws.id} value={ws.id}>{ws.name}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => { navigate('/onboarding'); maybeClose(); }}
            style={{
              marginTop: '8px', width: '100%', padding: '6px 10px',
              background: 'none', border: '1px dashed var(--paper-3)',
              borderRadius: 'var(--r)', cursor: 'pointer', fontSize: '12px',
              color: 'var(--ink-mute)', fontFamily: 'var(--sans)', textAlign: 'left',
            }}
          >
            + New business
          </button>
        </div>

        {/* Nav links */}
        <div style={{ padding: '12px 8px', flex: 1, overflowY: 'auto' }}>
          <div style={{
            fontSize: '10px', fontWeight: '500', letterSpacing: '0.1em',
            textTransform: 'uppercase', color: 'var(--ink-mute)',
            marginBottom: '6px', padding: '0 8px',
          }}>
            Workspace
          </div>
          <nav style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {navItems.map(({ icon, label, href }) => (
              <Link
                key={label} to={href} onClick={maybeClose}
                style={{
                  display: 'flex', alignItems: 'center', gap: '10px',
                  padding: '8px 10px', borderRadius: '6px', fontSize: '13px',
                  textDecoration: 'none',
                  color: isActive(href) ? 'var(--ink)' : 'var(--ink-soft)',
                  background: isActive(href) ? 'var(--paper-3)' : 'none',
                  fontWeight: isActive(href) ? '500' : '400',
                  transition: 'background 0.12s',
                }}
                onMouseEnter={e => { if (!isActive(href)) e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                onMouseLeave={e => { if (!isActive(href)) e.currentTarget.style.background = 'none'; }}
              >
                <span style={{ width: '18px', textAlign: 'center', opacity: 0.7, flexShrink: 0 }}>{icon}</span>
                <span style={{ flex: 1 }}>{label}</span>
                {label === 'Agents' && agentRunning && <AnimatedDot color="var(--teal)" size={6} />}
              </Link>
            ))}
          </nav>

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: '10px', padding: '10px 12px', marginBottom: '10px', marginTop: '10px',
            background: 'rgba(55,138,221,0.08)', border: '1px solid rgba(55,138,221,0.25)',
            borderRadius: 'var(--r)',
          }}>
            <p style={{ fontSize: '12px', fontWeight: '600', color: '#1a5fa0', margin: 0 }}>
              We know Founders are busy, so we brought AI CEO and AI employees to do your work.
            </p>
          </div>
        </div>

        {/* Bottom: settings + sign out */}
        <div style={{ padding: '8px 8px 16px', borderTop: '1px solid var(--paper-3)', flexShrink: 0 }}>
          <Link
            to="/workspace/settings" onClick={maybeClose}
            style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '8px 10px', borderRadius: '6px', fontSize: '13px',
              color: 'var(--ink-soft)', textDecoration: 'none', transition: 'background 0.12s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.04)'}
            onMouseLeave={e => e.currentTarget.style.background = 'none'}
          >
            <span style={{ width: '18px', textAlign: 'center', opacity: 0.7 }}>⚙</span>
            Settings
          </Link>
          <button
            onClick={() => { logout(); navigate('/'); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '8px 10px', borderRadius: '6px', fontSize: '13px',
              color: 'var(--accent)', background: 'none',
              border: '1px solid transparent', width: '100%',
              cursor: 'pointer', fontFamily: 'var(--sans)', transition: 'all 0.15s',
            }}
            onMouseEnter={e => { e.currentTarget.style.background = 'rgba(200,86,58,0.06)'; e.currentTarget.style.borderColor = 'rgba(200,86,58,0.2)'; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'none'; e.currentTarget.style.borderColor = 'transparent'; }}
          >
            <span style={{ width: '18px', textAlign: 'center' }}>→</span>
            Sign out
          </button>
        </div>
      </nav>
    </>
  );
}

function WsItem({ name, active, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '7px 10px', borderRadius: 'var(--r)', cursor: 'pointer',
        background: active ? 'var(--ink)' : 'var(--white)',
        border: `1px solid ${active ? 'var(--ink)' : 'var(--paper-3)'}`,
        fontSize: '13px', fontWeight: '500',
        color: active ? 'var(--paper)' : 'var(--ink)',
        transition: 'all 0.15s',
      }}
    >
      <div style={{
        width: '6px', height: '6px', borderRadius: '50%', flexShrink: 0,
        background: active ? 'var(--amber)' : 'var(--teal)',
      }} />
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TopNav
// ─────────────────────────────────────────────────────────────────────────────
// NEW: accepts `helpButton` prop — rendered in the right cluster before the avatar.
// When a HelpBanner is dismissed, pass its `navButton` here so the ? persists.
//
// Example:
//   const { banner, navButton } = useHelpBanner({ storageKey, title, message });
//   <TopNav helpButton={navButton} ... />

export function TopNav({ breadcrumb, drawerOpen, onToggleDrawer, drawerPinned = false, helpButton }) {
  const { user }            = useAuth();
  const { activeWorkspace } = useWorkspaces();

  const initials  = (user?.displayName || user?.email || 'U')[0].toUpperCase();
  const wsName    = activeWorkspace?.name || '';
  const wsInitial = wsName ? wsName[0].toUpperCase() : '';

  return (
    <header style={{
      height: '52px',
      background: 'rgba(245,242,235,0.92)',
      backdropFilter: 'blur(10px)',
      borderBottom: '1px solid var(--paper-3)',
      display: 'flex', alignItems: 'center',
      justifyContent: 'space-between',
      padding: '0 16px', gap: '12px',
      position: 'sticky', top: 0, zIndex: 100, flexShrink: 0,
      marginLeft: drawerPinned ? `${DRAWER_WIDTH}px` : 0,
      transition: 'margin-left 0.22s cubic-bezier(0.4,0,0.2,1)',
    }}>
      {/* Left: hamburger + logo + workspace + breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <button
          onClick={onToggleDrawer}
          aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
          style={{
            width: '34px', height: '34px', borderRadius: '8px',
            background: drawerOpen ? 'var(--paper-3)' : 'none',
            border: '1px solid transparent', cursor: 'pointer',
            display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: '4px',
            transition: 'all 0.15s', flexShrink: 0,
          }}
          onMouseEnter={e => e.currentTarget.style.background = 'var(--paper-3)'}
          onMouseLeave={e => e.currentTarget.style.background = drawerOpen ? 'var(--paper-3)' : 'none'}
        >
          <span style={{ display: 'block', width: '16px', height: '1.5px', background: 'var(--ink)', borderRadius: '2px' }} />
          <span style={{ display: 'block', width: '16px', height: '1.5px', background: 'var(--ink)', borderRadius: '2px' }} />
          <span style={{ display: 'block', width: '16px', height: '1.5px', background: 'var(--ink)', borderRadius: '2px' }} />
        </button>

        <Link to="/Dashboard" style={{ fontFamily: 'var(--serif)', fontSize: '20px', color: 'var(--ink)', textDecoration: 'none' }}>
          raji<span style={{ color: 'var(--accent)' }}>.</span>ai
        </Link>

        {wsName && wsName !== 'My Business' && (
          <>
            <div style={{ width: '1px', height: '18px', background: 'var(--paper-3)' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '500', color: 'var(--ink-soft)' }}>
              <div style={{
                width: '18px', height: '18px', borderRadius: '4px', background: 'var(--accent)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '9px', fontWeight: '700', color: '#fff', flexShrink: 0,
              }}>{wsInitial}</div>
              <span className="hide-xs">{wsName}</span>
            </div>
          </>
        )}

        {breadcrumb && (
          <>
            <span style={{ color: 'var(--paper-3)', fontSize: '14px' }}>›</span>
            <span style={{ fontSize: '13px', color: 'var(--ink-mute)', whiteSpace: 'nowrap' }}>{breadcrumb}</span>
          </>
        )}
      </div>

      {/* Right: helpButton (? after dismiss) + user avatar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        {helpButton}
        <div
          title="Menu"
          style={{
            width: '32px', height: '32px', borderRadius: '50%',
            background: 'var(--ink)', color: 'var(--paper)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '13px', fontWeight: '500', flexShrink: 0,
          }}
        >
          {initials}
        </div>
      </div>
    </header>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// useDrawer
// ─────────────────────────────────────────────────────────────────────────────
export function useDrawer() {
  const isMobile = useIsMobile();

  const [pinned, setPinned] = useState(() => {
    if (typeof window === 'undefined') return true;
    const stored = localStorage.getItem(PIN_STORAGE_KEY);
    return stored === null ? true : stored === 'true';
  });

  const [open, setOpen] = useState(() => !isMobile && pinned);

  useEffect(() => {
    if (isMobile) { setOpen(false); }
    else { setOpen(pinned); }
  }, [isMobile]);

  const toggle = useCallback(() => setOpen(o => !o), []);
  const close  = useCallback(() => setOpen(false), []);

  function togglePin() {
    const next = !pinned;
    setPinned(next);
    localStorage.setItem(PIN_STORAGE_KEY, String(next));
    if (!next) setOpen(false);
    else       setOpen(true);
  }

  return { open, toggle, close, pinned: isMobile ? false : pinned, togglePin };
}

// ─────────────────────────────────────────────────────────────────────────────
// PageBody
// ─────────────────────────────────────────────────────────────────────────────
export function PageBody({ children, style, drawerPinned = false }) {
  return (
    <main style={{
      flex: 1, overflowY: 'auto', padding: '24px 28px', minWidth: 0,
      marginLeft: drawerPinned ? `${DRAWER_WIDTH}px` : 0,
      transition: 'margin-left 0.22s cubic-bezier(0.4,0,0.2,1)',
      ...style,
    }}>
      {children}
    </main>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// BodyGrid
// ─────────────────────────────────────────────────────────────────────────────
export function BodyGrid({ children, rightBar, drawerPinned = false }) {
  return (
    <div style={{
      display: 'flex', flex: 1, minHeight: 0,
      marginLeft: drawerPinned ? `${DRAWER_WIDTH}px` : 0,
      transition: 'margin-left 0.22s cubic-bezier(0.4,0,0.2,1)',
    }}>
      {children}
      {rightBar}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SectionHeader
// ─────────────────────────────────────────────────────────────────────────────
export function SectionHeader({ title, actionLabel, actionHref, actionOnClick }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
      <h2 style={{ fontFamily: 'var(--serif)', fontSize: '18px', fontWeight: '400' }}>{title}</h2>
      {actionLabel && actionHref && (
        <Link to={actionHref} style={{ fontSize: '12px', color: 'var(--ink-mute)', textDecoration: 'none' }}>
          {actionLabel}
        </Link>
      )}
      {actionLabel && actionOnClick && (
        <button onClick={actionOnClick} style={{
          fontSize: '12px', color: 'var(--ink-mute)',
          background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--sans)',
        }}>
          {actionLabel}
        </button>
      )}
    </div>
  );
}

// Keep legacy exports as no-ops
export function TopBar() { return null; }
export function LeftNav() { return null; }

// ─────────────────────────────────────────────────────────────────────────────
// USAGE EXAMPLE — with HelpBanner
// ─────────────────────────────────────────────────────────────────────────────
//
//   import { AppShell, TopNav, NavDrawer, PageBody, useDrawer } from '../components/Layout';
//   import { useHelpBanner } from '../components/HelpBanner';
//
//   export default function CampaignPage() {
//     const drawer = useDrawer();
//     const help   = useHelpBanner({
//       storageKey: 'help-campaign-page',
//       title:      'Advertisement Campaign',
//       message:    'AI CEO has generated this campaign. Review your ad copy, preview each platform, export as PNG, and copy post text to publish to your channels.',
//     });
//
//     return (
//       <AppShell>
//         <TopNav
//           breadcrumb="Campaign"
//           drawerOpen={drawer.open}
//           onToggleDrawer={drawer.toggle}
//           drawerPinned={drawer.pinned}
//           helpButton={help.navButton}   // ← ? appears here after dismiss
//         />
//         <NavDrawer open={drawer.open} onClose={drawer.close} pinned={drawer.pinned} onTogglePin={drawer.togglePin} />
//         {help.banner}                   // ← renders below nav, above page content
//         <PageBody drawerPinned={drawer.pinned}>
//           {/* page content */}
//         </PageBody>
//       </AppShell>
//     );
//   }